import { HttpParams, HttpRequest } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { Injectable, Injector, PLATFORM_ID, inject } from '@angular/core';
import { Observable, firstValueFrom, timeout } from 'rxjs';

import { TeacherAssessmentService } from 'src/app/shared/services/api/teacher-assessment.service';
import { ConnectivityService } from '../connectivity.service';
import { DatabaseService } from '../database.service';
import { AssessmentResultEntity } from '../models/entities.model';
import { SyncApiService } from '../sync/sync-api.service';
import { HttpCacheRepository } from './http-cache.repository';
import { apiBase, cacheKeyFor, localFirstClass } from './offline-endpoints';

/**
 * URLs of the teacher screens whose responses are built on this device.
 *
 * They must equal what TeacherAssessmentService requests, or the screen would
 * never find what was built for it. offline-preload.spec.ts holds them to that
 * by calling the real service and comparing cache keys.
 */
export const TEACHER_ASSESSMENT_URLS = {
  sections(assessmentId: number, parentSectionId: number | null, attemptId: number | null): string {
    const params: Record<string, string> = {};
    if (parentSectionId) params['parent_section_id'] = String(parentSectionId);
    if (attemptId) params['attempt_id'] = String(attemptId);
    return urlWith(`api/v1/teachers/multiple-assessments/${assessmentId}/sections`, params);
  },

  sectionQuestions(sectionId: number, qType: string | null, attemptId: number | null): string {
    const params: Record<string, string> = {};
    if (qType) params['qType'] = qType;
    if (attemptId) params['attemptId'] = String(attemptId);
    return urlWith(`api/v1/teachers/multiple-assessments/section/${sectionId}/items`, params);
  },

  sectionItemOptions(sectionItemId: number, attemptId: number | null): string {
    const params: Record<string, string> = {};
    if (attemptId) params['attemptId'] = String(attemptId);
    return urlWith(
      `api/v1/teachers/multiple-assessments/section-item/${sectionItemId}/options`,
      params,
    );
  },
};

/** Whether the signed-in user is a teacher; only teachers use these screens. */
export function isTeacherSession(): boolean {
  try {
    const level = (localStorage.getItem('accessLevel') ?? '').toLowerCase();
    const role = (localStorage.getItem('role') ?? '').toLowerCase();
    return level === 'teacher' || (!level && role === 'teacher');
  } catch {
    return false;
  }
}

interface AssessmentRow {
  id: number;
  q_type?: string | null;
  options_as_layer?: number | string | null;
  classes?: Array<{ id: number }>;
}
interface SectionRow {
  id: number;
  has_children?: boolean | number;
  [field: string]: unknown;
}
interface ItemRow {
  id: number;
  [field: string]: unknown;
}
interface StudentRow {
  id: number;
  assignment_id?: number;
  attempt_id?: number | null;
}
interface Envelope<T> {
  data?: T;
  [field: string]: unknown;
}

/** What one assessment's structure looks like once fetched. */
interface AssessmentStructure {
  assessment: AssessmentRow;
  /** parent section id (null for the root) -> the sections response for it */
  sections: Map<number | null, Envelope<SectionRow[]>>;
  /** `${qType}|${sectionId}` -> the items response for it */
  items: Map<string, { sectionId: number; qType: string | null; body: Envelope<ItemRow[]> }>;
  /** section item id -> the options response for it */
  options: Map<number, Envelope<Record<string, unknown>>>;
  /** The qType the layered screens ask with, used for progress counts. */
  primaryQType: string | null;
}

/**
 * Downloads a teacher's assessments at sign-in, so every screen opens from this
 * device - including screens the teacher has never opened, and with no network.
 *
 * Two kinds of data, handled differently on purpose:
 *
 *   From the server  the assessment list, each assessment's section tree,
 *                    questions, options and techniques, the class interval
 *                    overviews, every page of each class's students, and each
 *                    started attempt's results. Results are the server's
 *                    scoring, so they are never recomputed here.
 *
 *   Built here       each student's own view of the sections and questions -
 *                    the same structure with that student's answers and
 *                    progress on it. Fetching those would cost a request per
 *                    student per section (thousands, for a school), when the
 *                    structure is already here and the answers were synced to
 *                    IndexedDB moments earlier by the entity stages.
 *
 * Built responses are stored as old, so the first time a screen is opened while
 * the API is reachable it still shows at once and is quietly replaced by the
 * server's own copy.
 */
@Injectable({ providedIn: 'root' })
export class OfflinePreloadService {
  private readonly browser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly injector = inject(Injector);
  private readonly databaseService = inject(DatabaseService);
  private readonly connectivity = inject(ConnectivityService);
  private readonly session = inject(SyncApiService);
  private readonly cache = inject(HttpCacheRepository);

  private static readonly CONCURRENCY = 6;
  private static readonly REQUEST_TIMEOUT_MS = 30_000;
  /** teacher-assessment-students asks for pages of this size. */
  private static readonly STUDENT_PAGE_SIZE = 10;
  private static readonly MAX_STUDENT_PAGES = 100;
  private static readonly MAX_SECTION_DEPTH = 8;

  /** The login-time stage that runs this - see SyncService.registerHydrationTask. */
  hydrationTask() {
    return {
      key: 'screens',
      labelKey: 'offline.loadingScreens',
      label: 'Preparing your assessments to work offline',
      run: (report: (records: number) => void) => this.preload(report),
    };
  }

  /**
   * Fetch and build everything. Resolves to the number of screens stored.
   *
   * Never throws for a single failed request: a screen that could not be
   * prepared is fetched the first time it is opened, as before.
   */
  async preload(report: (records: number) => void = () => undefined): Promise<number> {
    if (!this.browser || !this.databaseService.isAvailable || !isTeacherSession()) return 0;

    const userKey = this.session.currentUserKey();
    const schoolId = Number(localStorage.getItem('school-id'));
    if (!userKey || !schoolId) return 0;
    if (!this.connectivity.isOnline() && !(await this.connectivity.check())) return 0;

    const api = this.injector.get(TeacherAssessmentService);
    const startedAt = Date.now();
    let stored = 0;
    const count = () => report(++stored);

    // Everything this teacher already has is marked out of date first, so each
    // request below comes from the server instead of the copy it replaces.
    await this.cache.invalidate(userKey, (entry) => localFirstClass(entry.url) !== null);

    const stillSignedIn = () => this.session.currentUserKey() === userKey;
    const fetch = async <T>(request: Observable<T>): Promise<T | null> => {
      if (!stillSignedIn()) return null;
      try {
        const body = await firstValueFrom(
          request.pipe(timeout(OfflinePreloadService.REQUEST_TIMEOUT_MS)),
        );
        count();
        return body;
      } catch {
        return null;
      }
    };
    const build = async (url: string, body: unknown) => {
      if (!stillSignedIn()) return;
      // A screen the teacher opened while this was running already holds the
      // server's copy; a built one must not replace it.
      const key = cacheKeyFor(userKey, url);
      const existing = await this.cache.get(key);
      if (existing && !existing.invalidatedAt && existing.storedAt >= startedAt) return;
      await this.cache.put({ key, userKey, url, body, storedAt: 1 });
      count();
    };

    const list = await fetch<Envelope<{ assessments?: AssessmentRow[] }>>(
      api.getAssessmentsBySchool(schoolId),
    );
    await fetch(api.getSchoolConfigs(schoolId));

    for (const assessment of list?.data?.assessments ?? []) {
      if (!stillSignedIn()) break;
      const structure = await this.fetchStructure(assessment, api, fetch);
      const students = await this.fetchStudents(assessment, api, fetch);

      const started = students.filter((s) => Number(s.attempt_id) > 0);
      for (const student of started) {
        await this.buildAttemptScreens(structure, Number(student.attempt_id), build);
      }

      // Results are the server's scoring; fetch rather than build.
      await pool(started, OfflinePreloadService.CONCURRENCY, async (student) => {
        const attemptId = Number(student.attempt_id);
        await fetch(
          assessment.q_type === 'tap_multiple'
            ? api.getDetailedResults(attemptId)
            : api.getBmcLayer0Results(attemptId),
        );
      });
    }

    return stored;
  }

  // ── From the server ──────────────────────────────────────────────────────

  private async fetchStructure(
    assessment: AssessmentRow,
    api: TeacherAssessmentService,
    fetch: <T>(request: Observable<T>) => Promise<T | null>,
  ): Promise<AssessmentStructure> {
    // The layered screens ask with the assessment's own q_type; the linear-scale
    // screen asks with none. Both spellings are fetched where they differ.
    const primaryQType = assessment.q_type || null;
    const qTypes = unique<string | null>([
      primaryQType,
      ...(assessment.q_type === 'linear_scale' ? [null] : []),
    ]);

    const structure: AssessmentStructure = {
      assessment,
      sections: new Map(),
      items: new Map(),
      options: new Map(),
      primaryQType,
    };

    // Section tree, breadth first, the way the layers screen walks it.
    const seen = new Set<number>();
    let frontier: Array<number | null> = [null];
    for (let depth = 0; frontier.length && depth < OfflinePreloadService.MAX_SECTION_DEPTH; depth++) {
      const next: number[] = [];
      await pool(frontier, OfflinePreloadService.CONCURRENCY, async (parentId) => {
        const body = await fetch<Envelope<SectionRow[]>>(
          api.getAssessmentSections(assessment.id, parentId, null),
        );
        if (!body) return;
        structure.sections.set(parentId, body);
        for (const row of asArray<SectionRow>(body.data)) {
          if (row.has_children && !seen.has(row.id)) next.push(row.id);
          seen.add(row.id);
        }
      });
      frontier = next;
    }

    const sectionIds = [...structure.sections.values()].flatMap((body) =>
      asArray<SectionRow>(body.data).map((row) => row.id),
    );

    // Questions for every section - the layers screen may open questions at any
    // depth its layer config reaches - plus techniques where there are any.
    await pool(
      sectionIds.flatMap((sectionId) => qTypes.map((qType) => ({ sectionId, qType }))),
      OfflinePreloadService.CONCURRENCY,
      async ({ sectionId, qType }) => {
        const body = await fetch<Envelope<ItemRow[]>>(
          api.getSectionQuestions(sectionId, qType, null, null),
        );
        if (body) structure.items.set(`${qType}|${sectionId}`, { sectionId, qType, body });
      },
    );
    await pool(
      sectionIds.filter((id) => this.itemsOf(structure, id).length > 0),
      OfflinePreloadService.CONCURRENCY,
      async (sectionId) => {
        await fetch(api.getTechniques(sectionId));
      },
    );

    // Options, when this assessment presents them as a layer of their own.
    if (Number(assessment.options_as_layer) === 1) {
      const itemIds = unique(sectionIds.flatMap((id) => this.itemsOf(structure, id).map((i) => i.id)));
      await pool(itemIds, OfflinePreloadService.CONCURRENCY, async (itemId) => {
        const body = await fetch<Envelope<Record<string, unknown>>>(
          api.getSectionItemOptions(itemId, null),
        );
        if (body) structure.options.set(itemId, body);
      });
    }

    return structure;
  }

  /** Each class's interval overview and every page of its students. */
  private async fetchStudents(
    assessment: AssessmentRow,
    api: TeacherAssessmentService,
    fetch: <T>(request: Observable<T>) => Promise<T | null>,
  ): Promise<StudentRow[]> {
    const byAssignment = new Map<number, StudentRow>();

    await pool(assessment.classes ?? [], OfflinePreloadService.CONCURRENCY, async (klass) => {
      await fetch(api.getClassIntervals(assessment.id, klass.id));

      for (let page = 1; page <= OfflinePreloadService.MAX_STUDENT_PAGES; page++) {
        const body = await fetch<Envelope<{ students?: StudentRow[]; totalPages?: number }>>(
          api.getAssignedStudents(
            assessment.id,
            page,
            OfflinePreloadService.STUDENT_PAGE_SIZE,
            klass.id,
            null,
            null,
          ),
        );
        const rows = body?.data?.students ?? [];
        for (const student of rows) byAssignment.set(student.assignment_id ?? student.id, student);
        if (!rows.length || page >= Number(body?.data?.totalPages ?? 0)) break;
      }
    });

    return [...byAssignment.values()];
  }

  // ── Built here ───────────────────────────────────────────────────────────

  /**
   * One student's sections, questions and options, as the server would return
   * them for this attempt: the structure, with this attempt's answers and
   * progress written in. Mirrors getAssessmentSections, getAssessmentQuestions
   * and getSectionItemOptions in the teacher-multi-assessment backend.
   */
  private async buildAttemptScreens(
    structure: AssessmentStructure,
    attemptId: number,
    build: (url: string, body: unknown) => Promise<void>,
  ): Promise<void> {
    const db = await this.databaseService.ready();
    const answers = (await db.assessmentResults.where('attempt_id').equals(attemptId).toArray())
      .filter((answer) => !answer.deletedAt);
    const answerByItem = new Map<number, AssessmentResultEntity>(
      answers.map((answer) => [Number(answer.section_item_id), answer]),
    );

    // Sections, with the progress the server computes: a section's questions
    // plus those of its direct children, against the answers in this attempt.
    for (const [parentId, body] of structure.sections) {
      const data = asArray<SectionRow>(body.data).map((row) => {
        const itemIds = new Set<number>([
          ...this.itemsOf(structure, row.id).map((item) => Number(item.id)),
          ...asArray<SectionRow>(structure.sections.get(row.id)?.data).flatMap((child) =>
            this.itemsOf(structure, child.id).map((item) => Number(item.id)),
          ),
        ]);
        const total = itemIds.size;
        const answered = [...itemIds].filter((id) => answerByItem.has(id)).length;
        return {
          ...row,
          total_questions: total,
          answered_questions: answered,
          status:
            total === 0 || answered === 0
              ? 'not_started'
              : answered >= total
                ? 'completed'
                : 'in_progress',
        };
      });
      await build(
        TEACHER_ASSESSMENT_URLS.sections(structure.assessment.id, parentId, attemptId),
        { ...body, data },
      );
    }

    // Questions, with this attempt's answer joined onto each item.
    for (const { sectionId, qType, body } of structure.items.values()) {
      const data = asArray<ItemRow>(body.data).map((row) => {
        const answer = answerByItem.get(Number(row.id));
        return {
          ...row,
          response_json: answer ? parseJson(answer.response_json) : null,
          response_text: answer?.response_text ?? null,
          response_numeric: answer?.response_numeric ?? null,
          remark_text: answer?.remark_text ?? null,
        };
      });
      await build(TEACHER_ASSESSMENT_URLS.sectionQuestions(sectionId, qType, attemptId), {
        ...body,
        data,
      });
    }

    // Options, with the answer and each option's recorded clicks.
    for (const [itemId, body] of structure.options) {
      const question = body.data;
      if (!question || typeof question !== 'object') continue;

      const answer = answerByItem.get(itemId);
      const responseId = answer ? (answer.serverId ?? answer.id) : null;
      const clicks = new Map<number, unknown>();
      if (responseId) {
        const selected = await db.selectedOptions.where('response_id').equals(responseId).toArray();
        for (const row of selected) {
          if (!row.deletedAt) clicks.set(Number(row.option_id), row.clicks ?? null);
        }
      }

      const json = parseJson(answer?.response_json ?? null);
      const data = {
        ...question,
        response_id: responseId,
        response_json:
          json && typeof json === 'object' && (json as Record<string, unknown>)['option_id'] != null
            ? { ...(json as Record<string, unknown>), option_id: Number((json as Record<string, unknown>)['option_id']) }
            : json,
        response_numeric: answer?.response_numeric ?? null,
        remark_text: answer?.remark_text ?? null,
        options: asArray<Record<string, unknown>>(question['options']).map((option) => ({
          ...option,
          clicks: clicks.has(Number(option['id'])) ? clicks.get(Number(option['id'])) : null,
        })),
      };
      await build(TEACHER_ASSESSMENT_URLS.sectionItemOptions(itemId, attemptId), {
        ...body,
        data,
      });
    }
  }

  private itemsOf(structure: AssessmentStructure, sectionId: number): ItemRow[] {
    return asArray<ItemRow>(structure.items.get(`${structure.primaryQType}|${sectionId}`)?.body.data);
  }
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Build a request URL exactly as ApiService does: base, endpoint, HttpParams. */
function urlWith(path: string, params: Record<string, string>): string {
  return new HttpRequest('GET', `${apiBase()}/${path}`, null, {
    params: new HttpParams({ fromObject: params }),
  }).urlWithParams;
}

/** Run `worker` over `items`, at most `limit` at a time. */
async function pool<T>(
  items: readonly T[],
  limit: number,
  worker: (item: T) => Promise<void>,
): Promise<void> {
  let next = 0;
  const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (next < items.length) {
      const item = items[next++];
      await worker(item);
    }
  });
  await Promise.all(runners);
}

function unique<T>(values: readonly T[]): T[] {
  return [...new Set(values)];
}

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

/** The server parses response_json before returning it; entities may hold either form. */
function parseJson(value: unknown): unknown {
  if (typeof value !== 'string') return value ?? null;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}
